import { forwardRef } from "react";
import { ButtonProps } from "../../types";
import styles from "./Button.module.scss";

/**
 * Button Component
 *
 * Enterprise-grade button with multiple variants and loading states
 *
 * @example
 * ```tsx
 * <Button variant="primary" loading={isLoading}>
 *   Continue
 * </Button>
 *
 * <Button variant="secondary" leftIcon={<ArrowIcon />}>
 *   Go Back
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      className,
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`
          ${styles.button}
          ${styles[variant]}
          ${styles[size]}
          ${fullWidth ? styles.fullWidth : ""}
          ${loading ? styles.loading : ""}
          ${className || ""}
        `.trim()}
        aria-busy={loading}
        {...props}
      >
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <SpinnerIcon />
          </span>
        )}

        {!loading && leftIcon && (
          <span className={styles.leftIcon} aria-hidden="true">
            {leftIcon}
          </span>
        )}

        <span className={styles.content}>{children}</span>

        {!loading && rightIcon && (
          <span className={styles.rightIcon} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

// Spinner icon for loading state
const SpinnerIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={styles.spinnerSvg}
  >
    <circle
      cx="8"
      cy="8"
      r="6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="32"
      strokeDashoffset="8"
    />
  </svg>
);
